import { useState } from 'react';
import { Banner, BigButton, Empty, Label, Screen } from '../components/ui';
import { db, useLive } from '../lib/db';
import { relative } from '../lib/format';
import { flushNow, useSyncState } from '../lib/sync';
import type { OutboxItem } from '../lib/types';

/**
 * „Чакащи" — екранът, който прави офлайн режима заслужаващ доверие.
 * Шофьорът вижда точно какво още не е стигнало до офиса и защо.
 */
export function QueueScreen() {
  const sync = useSyncState();
  const [busy, setBusy] = useState(false);

  const items = useLive(
    async () => {
      const rows = await db.outbox.toArray();
      return rows
        .filter((item) => item.status !== 'done')
        .sort((a, b) => a.createdAt - b.createdAt);
    },
    [],
    [] as OutboxItem[],
  );

  const sent = useLive(
    async () => (await db.outbox.where('status').equals('done').toArray()).length,
    [],
    0,
  );

  return (
    <Screen title="Чакащи за изпращане" back>
      <div className="space-y-4">
        <Banner tone={sync.online ? 'go' : 'warn'}>
          {sync.online
            ? 'Има връзка. Изпращането върви само.'
            : 'Няма връзка. Всичко е записано на телефона и ще тръгне само, щом има обхват.'}
        </Banner>

        {items.length === 0 ? (
          <Empty>Няма нищо за изпращане. Изпратени наскоро: {sent}.</Empty>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id} className="rounded-2xl border-2 border-line p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xl font-semibold">{item.label}</span>
                  <span className="text-base text-muted">{relative(item.createdAt)}</span>
                </div>
                <div
                  className={`text-lg ${item.status === 'blocked' ? 'text-stop' : 'text-muted'}`}
                >
                  {item.status === 'blocked'
                    ? `Спряно: ${item.lastError ?? 'грешка'} — покажете на диспечера`
                    : item.attempts === 0
                      ? 'Чака ред'
                      : `Опит ${item.attempts}${item.lastError ? ` · ${item.lastError}` : ''}`}
                </div>
              </li>
            ))}
          </ul>
        )}

        <BigButton
          tone="brand"
          disabled={busy || !sync.online}
          onClick={() => {
            setBusy(true);
            void flushNow().finally(() => setBusy(false));
          }}
        >
          {busy ? 'Изпраща…' : 'Синхронизирай сега'}
        </BigButton>

        <div>
          <Label>Как работи</Label>
          <p className="text-lg text-muted">
            Всеки запис носи собствен номер, затова повторното изпращане не създава дубликат.
            Опитите се разреждат: 5 секунди, 15 секунди, 45 секунди и така до половин час.
          </p>
        </div>
      </div>
    </Screen>
  );
}
